import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import { MetricsTracker, PageMetricsProvider, USER_EVENTS } from '@sm/metrics';
import { ExperimentProvider } from '../Experiments';
import Helmet from '../Helmet';

import {
  SMFooter,
  SMHeader,
  MobileBanner,
  SessionTimeoutModal,
  CookieBanner,
  StaticConsumer
} from '../..';

const BasePage = ({
  color,
  headerVariant,
  footerVariant,
  errorCtrl,
  showBillingAddress,
  includeHeader,
  includeFooter,
  children,
  pageId,
  actionFlow,
  pageMetricsAttributes,
  showCreateSurveyButton,
  legacyWeb,
  sticky
}) => {
  // Load GTM tag on the client if it's not already loaded
  const [loadGTM, setState] = useState(false);
  useEffect(() => {
    if (!loadGTM) {
      /* eslint-disable */
      (function(w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);
      })(
        window,
        document,
        'script',
        'dataLayer',
        `${window.SM.MetricsTracker.config.gtmId}`
      );
      /* eslint-enable */
      setState(!loadGTM);
    }
  }, [loadGTM]);

  return (
    <StaticConsumer>
      {({ user, environment, locale, 'client-config': clientConfig, GDPR }) => {
        const { id: userId } = user;
        const {
          languageCode: language,
          countryCode: country,
          domain,
          tld
        } = environment;

        MetricsTracker.initialize({
          userId,
          dataAnalyticsAPIPath: clientConfig.dataAnalyticsAPIPath,
          loggingAPIPath: clientConfig.loggingAPIPath,
          country: environment.countryCode,
          legacyWeb,
          gtmId: clientConfig.gtmId
        });

        const helmetTrackClientChangeState = (
          newState,
          addedTags,
          removedTags
        ) => {
          if (
            Object.keys(addedTags).length > 0 ||
            Object.keys(removedTags).length > 0
          ) {
            // Metrics for GTM tracking SPA changes
            MetricsTracker.track({
              name: USER_EVENTS.VIRTUAL_PAGE_VIEW,
              data: {
                actionType: USER_EVENTS.VIRTUAL_PAGE_VIEW,
                actionFlow: pageId
              }
            });
          }
        };

        return (
          <ExperimentProvider>
            <Helmet onChangeClientState={helmetTrackClientChangeState}>
              <title>Welcome to SurveyMonkey!</title>
              <meta
                name="keywords"
                content="SurveyMonkey,online surveys,online survey,web surveys, web survey,market research,free,research,stats,statistics,polls,voting,analyze,tool,decisions,planning,strategy,Community,forms,formsite,workgroups,cgi,database"
              />
              <meta
                name="description"
                content="Create and publish online surveys in minutes, and view results graphically and in real time. SurveyMonkey provides free online questionnaire and survey software."
              />
              <meta charset="utf-8" />
              <meta name="msapplication-config" content="none" />
              <meta name="author" content="SurveyMonkey" />
              <link
                rel="apple-touch-icon"
                href="/apple-touch-icon-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="57x57"
                href="/apple-touch-icon-57x57-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="60x60"
                href="/apple-touch-icon-60x60-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="72x72"
                href="/apple-touch-icon-72x72-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="76x76"
                href="/apple-touch-icon-76x76-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="114x114"
                href="/apple-touch-icon-114x114-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="120x120"
                href="/apple-touch-icon-120x120-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="144x144"
                href="/apple-touch-icon-144x144-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="152x152"
                href="/apple-touch-icon-152x152-precomposed.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="180x180"
                href="/apple-touch-icon-180x180-precomposed.png"
              />
              <link rel="shortcut icon" href="/favicon.ico" />
            </Helmet>

            <PageMetricsProvider
              pageId={pageId}
              attributes={pageMetricsAttributes}
              GDPR={GDPR}
              user={user}
              countryCode={country}
            >
              <If condition={includeHeader}>
                <SMHeader
                  sticky={sticky}
                  isUserAuthenticated={user.isAuthenticated}
                  color={color}
                  variant={headerVariant}
                  lang={language}
                  isEUDC={locale.isEUDC}
                  actionFlow={actionFlow}
                  user={user}
                  showCreateSurveyButton={showCreateSurveyButton}
                />
              </If>
              <Choose>
                <When condition={errorCtrl}>
                  <Query
                    query={errorCtrl.query}
                    variables={errorCtrl.variables}
                    client={errorCtrl.apolloClient}
                  >
                    {({ loading, error, data }) =>
                      errorCtrl.handler(loading, error, data) || children
                    }
                  </Query>
                </When>
                <Otherwise>{children}</Otherwise>
              </Choose>
              <If condition={includeFooter}>
                <SMFooter
                  domain={domain}
                  lang={language}
                  country={country}
                  isUserAuthenticated={user.isAuthenticated}
                  showBillingAddress={showBillingAddress}
                  variant={footerVariant}
                  isEUDC={locale.isEUDC}
                  tld={tld}
                />
              </If>
              <If condition={user.isAuthenticated}>
                <>
                  <SessionTimeoutModal user={user} />
                  <MobileBanner
                    downloadLink={clientConfig.mobileBannerDownloadLink}
                  />
                </>
              </If>
              <CookieBanner GDPR={GDPR} />
            </PageMetricsProvider>
          </ExperimentProvider>
        );
      }}
    </StaticConsumer>
  );
};

BasePage.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
  includeHeader: PropTypes.bool,
  includeFooter: PropTypes.bool,
  errorCtrl: PropTypes.shape({
    query: PropTypes.object,
    variables: PropTypes.object,
    handler: PropTypes.func,
    apolloClient: PropTypes.object
  }),
  legacyWeb: PropTypes.string.isRequired,
  showBillingAddress: PropTypes.bool,
  headerVariant: PropTypes.string,
  footerVariant: PropTypes.string,
  pageId: PropTypes.string.isRequired,
  actionFlow: PropTypes.string,
  showCreateSurveyButton: PropTypes.bool,
  pageMetricsAttributes: PropTypes.shape({}), // there's no defined shape for page metric attributes
  sticky: PropTypes.bool
};

BasePage.defaultProps = {
  color: 'primary',
  errorCtrl: null,
  showBillingAddress: false,
  headerVariant: 'default',
  footerVariant: 'default',
  includeHeader: true,
  includeFooter: true,
  actionFlow: 'header',
  pageMetricsAttributes: null,
  showCreateSurveyButton: true,
  sticky: true
};

export default BasePage;
